import React, { useState, useEffect } from "react";
import axios from "axios";
import "./css/orgRegister.css";

export default function OrgRegister() {
  const token = new URLSearchParams(window.location.search).get("token");

  const [step, setStep] = useState(1);
  const [onboardingSessionId, setOnboardingSessionId] = useState(null);
  const [orgAdminEmail, setOrgAdminEmail] = useState("");
  const [orgNameSuggested, setOrgNameSuggested] = useState("");

  const [organizationName, setOrganizationName] = useState("");
  const [adminFirst, setAdminFirst] = useState("");
  const [adminLast, setAdminLast] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("USA");

  const [maxCpu, setMaxCpu] = useState(8);
  const [maxMemory, setMaxMemory] = useState(16);
  const [maxUsers, setMaxUsers] = useState(5);

  const [error, setError] = useState("");

  // Progress percentage
  const progress = {
    1: 25,
    2: 50,
    3: 75,
    4: 100,
    5: 100
  }[step] || 25;

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get("/api/org-register/validate", {
          params: { token }
        });

        if (res.data.success) {
          setOnboardingSessionId(res.data.onboarding_session_id);
          setOrgAdminEmail(res.data.invitation.org_admin_email);
          setOrgNameSuggested(res.data.invitation.org_name_suggested || "");
          setOrganizationName(res.data.invitation.org_name_suggested || "");
        } else {
          setError("Invalid or expired registration link.");
        }
      } catch (err) {
        setError("Invalid or expired registration link.");
      }
    };

    if (token) validate();
    else setError("Missing registration token.");
  }, [token]);

  const submitDetails = async () => {
    setError("");
    if (!organizationName || !adminFirst || !adminLast || !street || !city || !state || !zip) {
      setError("All fields are required.");
      return;
    }

    try {
      await axios.post("/api/org-register/details", {
        onboarding_session_id: onboardingSessionId,
        organization_name: organizationName,
        admin_first: adminFirst,
        admin_last: adminLast,
        admin_email: orgAdminEmail,
        address: { street, city, state, zip, country }
      });
      setStep(2);
    } catch (err) {
      setError("Failed to save details.");
    }
  };

  const simulateBilling = async () => {
    setError("");
    try {
      await axios.post("/api/org-register/billing/complete", {
        onboarding_session_id: onboardingSessionId
      });
      setStep(3);
    } catch (err) {
      setError("Billing simulation failed.");
    }
  };

  const submitUsage = async () => {
    setError("");
    try {
      await axios.post("/api/org-register/usage", {
        onboarding_session_id: onboardingSessionId,
        max_cpu: maxCpu,
        max_memory: maxMemory,
        max_concurrent_users: maxUsers
      });
      setStep(4);
    } catch (err) {
      setError("Failed to save usage limits.");
    }
  };

  const finalize = async () => {
    await axios.post("/api/org-register/finalize", {
      onboarding_session_id: onboardingSessionId
    });
    setStep(5);
  };


  const completeLegal = async () => {
    setError("");
    try {
      await axios.post("/api/org-register/legal", {
        onboarding_session_id: onboardingSessionId
      });

      await finalize(); // NEW

    } catch (err) {
      setError("Failed to complete legal step.");
    }
  };


  const stepClass = (n) => {
    if (step > n) return "step completed";
    if (step === n) return "step active";
    return "step";
  };

  return (
    <div className="form-container">
      <h1 className="step-header">CloudGenomics — Organization Registration</h1>
      <p className="step-subtitle">
        Complete the steps below to register your organization and configure billing and usage limits.
      </p>

      <div className="progress-bar-wrapper">
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="step-indicator">
        <div className={stepClass(1)}>1</div>
        <div className={stepClass(2)}>2</div>
        <div className={stepClass(3)}>3</div>
        <div className={stepClass(4)}>4</div>
      </div>

      {error && <p className="error-text">{error}</p>}

      {step === 1 && (
        <div className="form-section">
          <h2>Organization Details</h2>

          <label>Organization Name</label>
          <input
            className="input-field"
            value={organizationName}
            onChange={e => setOrganizationName(e.target.value)}
          />

          <label>Admin First Name</label>
          <input
            className="input-field"
            value={adminFirst}
            onChange={e => setAdminFirst(e.target.value)}
          />

          <label>Admin Last Name</label>
          <input
            className="input-field"
            value={adminLast}
            onChange={e => setAdminLast(e.target.value)}
          />

          <label>Admin Email</label>
          <input className="input-field" value={orgAdminEmail} readOnly />

          <h3>Address</h3>

          <label>Street</label>
          <input
            className="input-field"
            value={street}
            onChange={e => setStreet(e.target.value)}
          />

          <label>City</label>
          <input
            className="input-field"
            value={city}
            onChange={e => setCity(e.target.value)}
          />

          <label>State</label>
          <input
            className="input-field"
            value={state}
            onChange={e => setState(e.target.value)}
          />

          <label>Zip</label>
          <input
            className="input-field"
            value={zip}
            onChange={e => setZip(e.target.value)}
          />

          <label>Country</label>
          <input
            className="input-field"
            value={country}
            onChange={e => setCountry(e.target.value)}
          />

          <button className="button-primary" onClick={submitDetails}>
            Continue
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="form-section">
          <h2>Billing Setup</h2>
          <p>
            This is a placeholder for Stripe Checkout. In production, this step will redirect to Stripe
            for secure billing setup and subscription creation.
          </p>

          <button className="button-primary" onClick={simulateBilling}>
            Simulate Billing Success
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="form-section">
          <h2>Usage Limits</h2>

          <label>Max CPU</label>
          <input
            className="input-field"
            type="number"
            value={maxCpu}
            onChange={e => setMaxCpu(e.target.value)}
          />

          <label>Max Memory (GB)</label>
          <input
            className="input-field"
            type="number"
            value={maxMemory}
            onChange={e => setMaxMemory(e.target.value)}
          />

          <label>Max Concurrent Users</label>
          <input
            className="input-field"
            type="number"
            value={maxUsers}
            onChange={e => setMaxUsers(e.target.value)}
          />

          <button className="button-primary" onClick={submitUsage}>
            Continue
          </button>
        </div>
      )}

      {step === 4 && (
        <div className="form-section">
          <h2>Legal Terms & Conditions</h2>
          <p>
            Please review the CloudGenomics legal terms. For now, this links to a placeholder document.
          </p>
          <p>
            <a href="/legal" target="_blank" rel="noreferrer">
              View Legal Document
            </a>
          </p>

          <button className="button-primary" onClick={completeLegal}>
            I Agree & Complete Registration
          </button>
        </div>
      )}

      {step === 5 && (
        <div className="form-section completed-message">
          <h2>Registration Complete</h2>
          <p>Your organization has been successfully registered.</p>
          <p>A temporary password has been emailed to you.</p>
        </div>
      )}

    </div>
  );
}